import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { authGuard } from "./auth/guards/auth.guard";
import { LogoutComponent } from "./components/logout/logout.component";
import { LoginComponent } from "./components/login/login.component";
import { ReportEmbedComponent } from "./components/report-embed/report-embed.component";
import { ACCESS_CONTROL } from "./access-control/access-control";
import { pageAccessGuard } from "./routing/guards/page-access.guard";
import { landingPageRedirectGuard } from "./routing/guards/landing-page-redirect.guard";
import { LandingPagePlaceholderComponent } from "./routing/landing-page/landing-page-placeholder.component";
import { RoleAssignmentRequiredComponent } from "./components/role-assignment-required/role-assignment-required.component";
import { roleAssignmentRequiredGuard } from "./routing/guards/role-assignment-required.guard";

const routes: Routes = [
    {
        path: "login",
        component: LoginComponent,
        data: {
            pageTitle: "Login",
        },
    },
    {
        path: "login/:connection",
        component: LoginComponent,
        data: {
            pageTitle: "Login",
        },
    },
    {
        path: "logout",
        component: LogoutComponent,
        data: {
            pageTitle: "Logout",
        },
    },
    {
        path: "",
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        children: [
            {
                path: "",
                pathMatch: "full",
                component: LandingPagePlaceholderComponent,
                canActivate: [landingPageRedirectGuard],
            },
            {
                path: "role-assignment-required",
                component: RoleAssignmentRequiredComponent,
                canActivate: [roleAssignmentRequiredGuard],
                data: {
                    pageTitle: "Access restricted",
                },
            },
            {
                path: "dashboard",
                loadChildren: () => import("./modules/dashboard").then((m) => m.AppDashboardModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.DASHBOARD)],
                data: {
                    pageTitle: "Dashboard",
                },
            },
            {
                path: "reports",
                component: ReportEmbedComponent,
                canActivate: [pageAccessGuard(ACCESS_CONTROL.REPORTS)],
                data: {
                    pageTitle: "Reports",
                },
            },
            {
                path: "reports/:key",
                component: ReportEmbedComponent,
                canActivate: [pageAccessGuard(ACCESS_CONTROL.REPORTS)],
                data: {
                    pageTitle: "Reports",
                },
            },
            {
                path: "browse",
                loadComponent: () => import("./modules/browse/browse.component").then((m) => m.BrowseComponent),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.BROWSE)],
                data: {
                    pageTitle: "Browse pages",
                },
            },
            {
                path: "help",
                loadChildren: () => import("./modules/help").then((m) => m.AppHelpModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.HELP)],
                data: {
                    pageTitle: "Help",
                },
            },
            {
                path: "certificates/request",
                loadChildren: () => import("./modules/certificate-request").then((m) => m.AppCertificateRequestModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.CERTIFICATE_MANAGEMENT)],
                data: {
                    pageTitle: "Certificate Request",
                },
            },
            {
                path: "certificates/upload",
                loadChildren: () => import("./modules/certificate-upload").then((m) => m.AppCertificateUploadModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.CERTIFICATE_MANAGEMENT)],
                data: {
                    pageTitle: "Certificate Upload",
                },
            },
            {
                path: "certificates",
                loadChildren: () => import("./modules/certificates").then((m) => m.AppCertificatesModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.CERTIFICATES)],
                data: {
                    pageTitle: "Certificates",
                },
            },
            {
                path: "logs",
                loadChildren: () => import("./modules/logs").then((m) => m.AppLogsModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.LOGS)],
                data: {
                    pageTitle: "Logs",
                },
            },
            {
                path: "accounts",
                loadChildren: () => import("./modules/accounts").then((m) => m.AppAccountsModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.ACCOUNTS)],
                data: {
                    pageTitle: "Accounts",
                },
            },
            {
                path: "users",
                loadChildren: () => import("./modules/users").then((m) => m.AppUsersModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.USERS)],
                data: {
                    pageTitle: "Users",
                },
            },
            {
                path: "cas",
                loadChildren: () =>
                    import("./modules/certificate-authorities").then((m) => m.AppCertificateAuthoritiesModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.CERTIFICATE_AUTHORITIES)],
                data: {
                    pageTitle: "Certificate Authorities",
                },
            },
            {
                path: "requests",
                loadChildren: () => import("./modules/requests-queue").then((m) => m.AppRequestsQueueModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.REQUESTS)],
                data: {
                    pageTitle: "Requests Queue",
                },
            },
            {
                path: "profile",
                loadChildren: () => import("./modules/profiles").then((m) => m.AppProfilesModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.PROFILE)],
                data: {
                    pageTitle: "Profile",
                },
            },
            {
                path: "domains",
                loadChildren: () => import("./modules/domains").then((m) => m.DomainsModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.DOMAINS)],
                data: {
                    pageTitle: "Domains",
                },
            },
            {
                path: "admin/accounts",
                loadChildren: () => import("./modules/admin/accounts").then((m) => m.AppAccountAdminModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.ADMIN)],
                data: {
                    pageTitle: "Account Administration",
                },
            },
            {
                path: "admin/reports",
                loadChildren: () => import("./modules/admin/reports").then((m) => m.AppReportAdminModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.ADMIN)],
                data: {
                    pageTitle: "Available Reports",
                },
            },
            {
                path: "tlsdiscovery",
                loadChildren: () => import("./modules/tls-discovery").then((m) => m.AppTlsDiscoveryModule),
                canActivate: [pageAccessGuard(ACCESS_CONTROL.TLS_DISCOVERY)],
                data: {
                    pageTitle: "TLS Discovery",
                },
            },
            {
                path: "**",
                redirectTo: "/",
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule {}
